import React from 'react';

/* ──────────────────────────────────────────────────────────────────
 * The contract value drop-down (Duncan 2026-11-03): what makes up a job's contract on file -- the SOV on the pay apps,
 * its freeform invoices, what's not billed yet -- laid out like the Job Cost tiles' Travel / Risk summaries. Opens from the
 * job's top bar and the Job Cost tabs' Contract Value tiles. The words for each finding live here too, so the SOV tab's
 * contract check card says the same thing.
 * ────────────────────────────────────────────────────────────────── */

export const money = (v) => `$${Math.abs(Number(v) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const plural = (n, word) => `${n} ${word}${n === 1 ? '' : 's'}`;

/** What the breakdown found: its tone (success / accent / warning) and the words. */
export const findingOf = (b) => ({
  adds_up: { tone: 'success', text: 'Adds up' },
  still_to_bill: { tone: 'info', text: `Adds up · ${money(b.not_billed)} still to bill` },
  never_billed: { tone: 'warning', text: `Fully billed, ${money(b.not_billed)} never invoiced` },
  over: { tone: 'warning', text: `${money(b.not_billed)} more than the contract on file` },
}[b.status] || { tone: 'info', text: '' });

export const sovSubOf = (b) => (b.sov.source === 'ce'
  ? `${plural(b.sov.pay_apps, 'pay app')} in ComputerEase`
  : b.sov.pay_apps ? `${plural(b.sov.pay_apps, 'pay app')} in Summit` : 'Not billed yet');

/** The change orders that make up what's not billed. CE's descriptions usually carry their own number ("CO 2 - ..."). */
export const reasonOf = (b) => (b.reason || []).map((c) => {
  const desc = String(c.description || '').trim();
  return `${/^C\.?O\.?\s*#?\s*\d/i.test(desc) ? desc : `CO ${c.co}${desc ? ` ${desc}` : ''}`} (approved)`;
}).join(' · ');

export const docLabelOf = (d) => {
  const text = String(d.label || '').trim();
  return `${text ? `${text.length > 38 ? `${text.slice(0, 38)}…` : text} · ` : ''}inv ${d.number}`;
};

export const summitSovNoteOf = (b) => (b.summit_sov
  ? `Summit's own SOV (${money(b.summit_sov.total)}) is ${money(b.summit_sov.difference)} ${b.summit_sov.difference > 0 ? 'more' : 'less'} than ComputerEase's.`
  : null);

export const toneColors = {
  success: { fg: 'var(--success)', soft: 'var(--success-soft)', ring: 'color-mix(in oklch, var(--success) 45%, transparent)' },
  warning: { fg: 'var(--warning)', soft: 'var(--warning-soft)', ring: 'color-mix(in oklch, var(--warning) 50%, transparent)' },
  // Still to bill: a slate blue, apart from the sage accent and the green "adds up" (Summit has no blue token; mixed
  // into the surface so it holds in dark mode too).
  info: {
    fg: 'oklch(0.55 0.11 250)', soft: 'color-mix(in oklch, oklch(0.62 0.12 250) 10%, var(--bg-surface))',
    ring: 'color-mix(in oklch, oklch(0.62 0.12 250) 42%, transparent)',
  },
};

const Row = ({ name, sub, amount, total, color }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '9px 14px', borderTop: '1px solid var(--border-subtle)', background: total ? 'var(--bg-surface-2)' : 'transparent' }}>
    <div style={{ minWidth: 0 }}>
      <div style={{ fontSize: 13.5, fontWeight: total ? 700 : 600, color: 'var(--text-1)', whiteSpace: 'nowrap' }}>{name}</div>
      {sub && <div style={{ fontSize: 12, color: 'var(--text-3)', lineHeight: 1.35 }}>{sub}</div>}
    </div>
    <div className="mono" style={{ flex: '0 0 auto', fontSize: 15, fontWeight: 800, color: color || 'var(--text-1)', fontVariantNumeric: 'tabular-nums', letterSpacing: '-0.02em' }}>{amount}</div>
  </div>
);

const ContractBreakdownSummary = ({ id, breakdown: b, onClose, onOpenSov, style, onMouseEnter, onMouseLeave }) => {
  const over = b.status === 'over';
  const docs = (b.own && b.own.documents) || [];
  const finding = findingOf(b);
  const tone = toneColors[finding.tone];
  const note = summitSovNoteOf(b);
  return (
    <div id={id} role="dialog" aria-label="Contract value breakdown" tabIndex={-1} onMouseEnter={onMouseEnter} onMouseLeave={onMouseLeave}
      style={{ position: 'absolute', top: 'calc(100% + 6px)', left: 0, zIndex: 40, width: 400, maxWidth: 'calc(100vw - 48px)', background: 'var(--bg-surface)', border: '1px solid var(--border-default)', borderRadius: 12, boxShadow: 'var(--shadow-md)', overflow: 'hidden', outline: 'none', textAlign: 'left', ...style }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 12px 10px 14px', background: 'var(--bg-surface-2)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 13.5, fontWeight: 600, color: 'var(--text-1)' }}>
          Contract value <span style={{ fontWeight: 500, color: 'var(--text-3)' }}>· what makes it up</span>
        </span>
        {onClose && (
          <button type="button" aria-label="Close" onClick={onClose} style={{ width: 22, height: 22, borderRadius: 6, color: 'var(--text-3)', display: 'inline-flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', background: 'transparent', border: 'none' }}>
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" aria-hidden="true"><path d="M6 6l12 12M6 18 18 6" /></svg>
          </button>
        )}
      </div>
      <Row name="SOV on the pay apps" sub={sovSubOf(b)} amount={money(b.sov.total)} />
      <Row name="Freeform invoices" sub={docs.length ? `${docs.slice(0, 2).map((d) => docLabelOf(d)).join(' · ')}${docs.length > 2 ? ` · and ${plural(docs.length - 2, 'more')}` : ''}` : 'None'}
        amount={money(b.own.total)} color={b.own.total ? undefined : 'var(--text-4)'} />
      <Row name={over ? 'Over the contract' : 'Not billed yet'} sub={reasonOf(b) || null}
        amount={money(Math.abs(b.not_billed) > 1 ? b.not_billed : 0)}
        color={over || b.status === 'never_billed' ? 'var(--warning)' : Math.abs(b.not_billed) > 1 ? undefined : 'var(--text-4)'} />
      <Row total name="Contract on file" amount={money(b.contract)} />
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '8px 12px 8px 14px', borderTop: '1px solid var(--border-subtle)', background: 'var(--bg-surface-2)' }}>
        <span style={{ flex: 1, minWidth: 0, fontSize: 12.5, fontWeight: 600, color: tone.fg }}>
          {finding.text}{note && <span style={{ display: 'block', fontWeight: 500, color: 'var(--warning)' }}>{note}</span>}
        </span>
        {onOpenSov && (
          <button type="button" onClick={onOpenSov} style={{ flex: '0 0 auto', font: 'inherit', fontSize: 12.5, fontWeight: 600, color: 'var(--accent-text)', background: 'transparent', border: 'none', cursor: 'pointer', padding: 0 }}>
            Open SOV tab ›
          </button>
        )}
      </div>
    </div>
  );
};

export default ContractBreakdownSummary;
